import "@elements/Advertisement/onsite-checkout.css";

import CASBlock from "@components/Blocks/CASBlock/CASBlock";
import TranslationPopover from "@components/Modules/Translation/TranslationPopover";
import WordPuzzle from "@components/Modules/WordPuzzle/WordPuzzle";
import { pubPageAdTargeting } from "@config/ads";
import { makeSvgDataSrc } from "@elements/Advertisement/helper/makeSvgData";
import Taboola from "@elements/Advertisement/Taboola";
import { getTagsName } from "@elements/GAData/GADataHelper";
import {
  CAS_UGC_SECTION_BANNER_ID,
  IS_PLAYWRIGHT_TEST,
} from "@helper/getEnvVariables";
import { getGTMData, getMetaData } from "@helper/getMetaData";
import useAdsControl from "@hooks/useAdsControl";
import { useBHVirtualPVAnalyticsCall } from "@hooks/useAnalyticsCall/useBHVirtualPVAnalyticsCall";
import useShortFormVideo from "@hooks/useBrightcoveVideo/useShortFormVideo";
import { useGrapeshotData } from "@hooks/useGrapeshot";
import { Route } from "@route-types/Article/+types/Article";
import BHArticleBasePage, {
  AdsSettings,
  BHArticleBasePageProps,
} from "@routes/BHArticleBasePage";
import ErrorPage from "@routes/ErrorPage";
import NotFound from "@routes/NotFound";
import { fetchContext, TRouteWithRedirect } from "@sphtech/web2-core/ssr";
import { ResponseType } from "@sphtech/web2-core/ssr";
import { generateContextForPreviewUrl } from "@src/app/cuePreview";
import { ProcessedArticleData } from "@transformer/useOSResponse";
import {
  AdTargetingType,
  ArticleAdTargetKeys,
  PageAdTargetingTypeEnum,
} from "@typings/Ads.d";
import { useEffect, useRef, useState } from "react";
import { ClientLoaderFunctionArgs, useLoaderData } from "react-router";

import { fetchArticleData, PayloadResponseType } from "./Article.server";
import ArticleInfiniteScroll from "./ArticleInfiniteScroll";
import ArticleContentCardNew from "./ArticleLayout/ArticleContentCardNew";
import ArticleMetaData from "./ArticleMetaData";
import ArticleElementWrapper from "./Components/ArticleElementWrapper";
import ShortFormVideos from "./Components/ShortFormVideos";

export function meta({ loaderData }: Route.MetaArgs) {
  const dataPayload =
    typeof loaderData === "object" && "payload" in loaderData
      ? loaderData.payload
      : [];
  const currentArticle =
    typeof dataPayload == "object" &&
    dataPayload != null &&
    "article" in dataPayload
      ? dataPayload.article
      : null;
  if (currentArticle == null) {
    return [{ title: "Page not found" }];
  }
  const { metaTags } = ArticleMetaData({
    article: currentArticle,
    pageNumber: 0,
    gsChannels: [],
  });

  const metaRes = getMetaData(metaTags);
  return [...metaRes];
}

export default function ArticlePage() {
  useAdsControl();

  const dataLoaderResponse: TRouteWithRedirect<PayloadResponseType, string> =
    useLoaderData();

  const [currentArticle, setCurrentArticle] = useState<ProcessedArticleData>();
  // eslint-disable-next-line
  const [_pagination, setPagination] = useState<number>(0);
  const [resolvedGsChannels, setResolvedGsChannels] = useState<string[]>([]);
  const { gsChannels, isLoading } = useGrapeshotData(
    dataLoaderResponse.type === ResponseType.SUCCESS
      ? (dataLoaderResponse.payload.article.urlPath as string)
      : "",
  );
  const analyticsCall = useBHVirtualPVAnalyticsCall();
  const containerRef = useRef<HTMLDivElement>(null);
  const { playing, videos, handleOnClickThumbnail, handleOnEndedVideo } =
    useShortFormVideo({ containerRef });
  const containerRefLong = useRef<HTMLDivElement>(null);

  // update when gsChannels are loaded
  useEffect(() => {
    if (!isLoading && gsChannels.length) {
      window.gs_channels = gsChannels;
      setResolvedGsChannels(gsChannels);
    }
  }, [isLoading, gsChannels]);

  if (dataLoaderResponse.type === ResponseType.CLIENT_ERROR) {
    return <NotFound />;
  } else if (dataLoaderResponse.type === ResponseType.SERVER_ERROR) {
    return <ErrorPage message="Something went wrong" />;
  }

  if (dataLoaderResponse.type === ResponseType.SUCCESS) {
    const pageAdTargetValue = PageAdTargetingTypeEnum.ARTICLE;
    const PubPageAdTarget = pubPageAdTargeting[
      pageAdTargetValue
    ] as AdTargetingType<ArticleAdTargetKeys, string>;
    const mainArticle = dataLoaderResponse.payload.article;

    const handleNewArticle = (
      newArticle: ProcessedArticleData,
      pageNumber: number,
      viewPortChange: boolean,
    ) => {
      setCurrentArticle(newArticle);
      setPagination(pageNumber);
      if (viewPortChange) {
        setTimeout(() => {
          analyticsCall(
            newArticle.title,
            undefined,
            undefined,
            undefined,
            pageNumber,
          );
        }, 300);
      }
    };

    const bhTags = getTagsName(mainArticle.tags || []);
    const bhArticleId = mainArticle.id;
    const articleSlotTarget: AdTargetingType<ArticleAdTargetKeys, string>[] = [
      { key: ArticleAdTargetKeys.ARTICLE_ID, value: bhArticleId },
      { key: ArticleAdTargetKeys.TAGS, value: bhTags.toLowerCase() },
      PubPageAdTarget,
    ];

    const adsUniqueSectionName = mainArticle.adsUniqueSectionName;

    const basePageProps: BHArticleBasePageProps = {
      adsUniqueSectionName,
      gsChannels: resolvedGsChannels,
      articleSlotTarget,
      prestitialPageTargeting: pageAdTargetValue,
      articleId: currentArticle ? currentArticle.id : mainArticle.id,
    };

    const rednerArticlePage = (isTaboolaEnabled: boolean) => {
      return (
        <>
          {adsUniqueSectionName == "mata-mata" && (
            <div className="bg-white-200 flex flex-col xl:flex-row items-center justify-center mb-5 py-md min-h-[200px] lg:min-h-[220px]">
              {IS_PLAYWRIGHT_TEST === "true" ? (
                <div>
                  <img
                    className="max-w-full"
                    src={makeSvgDataSrc({
                      width: 860,
                      height: 200,
                      text: "CAS Banner",
                    })}
                    alt="cas-banner"
                  />
                </div>
              ) : (
                <CASBlock casId={CAS_UGC_SECTION_BANNER_ID} />
              )}
            </div>
          )}
          <div id={mainArticle.id} className="article-card mt-5">
            <ArticleContentCardNew
              mainArticle={mainArticle}
              pageNumber={0}
              isInfiniteScrollArticle={false}
              gsChannels={resolvedGsChannels}
              articleSlotTarget={articleSlotTarget}
              visibleArticleId={
                currentArticle ? currentArticle.id : mainArticle.id
              }
            />
            <ArticleElementWrapper>
              <ShortFormVideos
                containerRef={containerRef}
                videos={videos}
                handleOnClickThumbnail={handleOnClickThumbnail}
                handleOnEndedVideo={handleOnEndedVideo}
                playing={playing}
              />
              <div className="mt-4 lg:mt-6">
                {isTaboolaEnabled && mainArticle.urlPath && (
                  <Taboola
                    counter={0}
                    pathName={mainArticle.urlPath}
                    pageType="article"
                    mode="thumbnails-base-widget"
                    container="below-article-thumbnails"
                    placement="Below Article Thumbnails"
                    targetType="mix"
                  />
                )}
              </div>
            </ArticleElementWrapper>
          </div>
        </>
      );
    };

    return (
      <>
        <BHArticleBasePage {...basePageProps}>
          {(adsSettings: AdsSettings) => (
            <>
              {rednerArticlePage(adsSettings.isTaboolaEnabled || false)}
              <ArticleInfiniteScroll
                mainArticle={mainArticle}
                onNewArticle={handleNewArticle}
                isTaboolaEnabled={adsSettings.isTaboolaEnabled || false}
                PubPageAdTarget={PubPageAdTarget}
                containerRef={containerRef}
                videos={videos}
                handleOnClickThumbnail={handleOnClickThumbnail}
                handleOnEndedVideo={handleOnEndedVideo}
                playing={playing}
                containerRefLong={containerRefLong}
              />
              <TranslationPopover
                gsChannels={resolvedGsChannels}
                articleSlotTarget={articleSlotTarget}
              />
              <WordPuzzle isCatfishEnabled={adsSettings.isCatfishEnabled} />
            </>
          )}
        </BHArticleBasePage>
      </>
    );
  }
}

export async function loader({
  request,
  params,
  context,
}: Route.LoaderArgs): Promise<
  TRouteWithRedirect<ProcessedArticleData | null, string>
> {
  if (context.previewData) {
    const result = generateContextForPreviewUrl(context.previewData);
    return result;
  }
  const url = new URL(request.url, `http://localhost`);
  const pathname = url.pathname;
  let data = [];

  if (pathname.includes("article")) {
    // Article ID-based route: /article/:id
    data = await fetchArticleData(params);
  } else {
    // URL-based routes: all other variants (/:section/:id OR /:parent/:section/:id)
    data = await fetchArticleData(params);
  }
  const dataPayload =
    typeof data === "object" && "payload" in data ? data.payload : [];
  const article =
    typeof dataPayload == "object" &&
    dataPayload != null &&
    "article" in dataPayload
      ? dataPayload.article
      : null;
  if (!article) {
    return { ...data };
  }
  const { gaData, metaTags } = ArticleMetaData({
    article: article,
    pageNumber: 0,
    gsChannels: [],
  });
  const gtmData = getGTMData(gaData);

  return {
    ...data,
    gtm: gtmData,
    jsonLd: metaTags.jsonLd,
  };
}

export async function clientLoader(args: ClientLoaderFunctionArgs) {
  const result = (await fetchContext(args)) as { context?: unknown };

  if (!result.context) {
    console.error(
      "Error occured during client-side routing, returning server Loader Data",
    );
    return await args.serverLoader();
  }

  const typedResult: unknown = result.context;

  // Check for valid payload field
  if (
    !typedResult ||
    typeof typedResult !== "object" ||
    !("payload" in typedResult) ||
    !Array.isArray(typedResult.payload)
  ) {
    console.error("Client Loader: fetchContext() returned an invalid payload");
  }
  return typedResult as Promise<
    TRouteWithRedirect<PayloadResponseType, string>
  >;
}
